import { getPipelineState, getRecentExecutions } from "./pipeline";
import { getAllVersions, markDeployed, markFailed } from "./versions";
import { updateItem, Tables } from "./db";
import type { Stage, VersionStatus } from "../types";

/**
 * Reconciler — syncs version status with actual pipeline state.
 * 
 * Key behavior:
 * - If a deploy stage succeeded → version is "deployed" at that stage
 * - If pipeline is waiting at an approval gate → version is "deployed" at the PREVIOUS stage, awaiting promotion
 * - If a deploy stage is actively running → version is "deploying" at that stage
 * - If execution failed/stopped → version is "failed"
 */
export async function reconcilePipelineState(): Promise<void> {
  const [versions, stages, executions] = await Promise.all([
    getAllVersions(),
    getPipelineState(),
    getRecentExecutions(5),
  ]);

  // Find versions that are currently deploying, queued, or awaiting promotion
  const activeVersions = versions.filter(
    (v) => v.status === "deploying" || v.status === "queued" || v.status === "awaiting-promotion"
  );

  if (activeVersions.length === 0) return;

  for (const version of activeVersions) {
    if (!version.pipelineExecutionId) continue;

    // Find this execution in recent executions
    const execution = executions.find(
      (e) => e.pipelineExecutionId === version.pipelineExecutionId
    );

    if (!execution) continue;

    // Execution failed or stopped
    if (execution.status === "Failed" || execution.status === "Stopped") {
      await markFailed(version.id);
      continue;
    }

    // Execution fully succeeded (reached the end of pipeline)
    if (execution.status === "Succeeded") {
      const furthestStage = getLastSucceededDeployStage(stages);
      if (furthestStage) {
        await markDeployed(version.id, furthestStage);
      }
      continue;
    }

    // Execution is in progress — determine the real state
    if (execution.status === "InProgress") {
      const state = analyzeCurrentPipelinePosition(stages);

      if (state.type === "deploying") {
        // Actively deploying to a stage right now
        await updateItem(Tables.VERSIONS, { id: version.id }, {
          stage: state.stage,
          status: "deploying" as VersionStatus,
        });
      } else if (state.type === "awaiting-approval") {
        // Deployed to a stage, waiting for approval to move to next
        await updateItem(Tables.VERSIONS, { id: version.id }, {
          stage: state.deployedStage,
          status: "awaiting-promotion" as VersionStatus,
          deployedAt: new Date().toISOString(),
        });
      } else if (state.type === "building") {
        // Still building, not at any deploy stage yet
        await updateItem(Tables.VERSIONS, { id: version.id }, {
          status: "deploying" as VersionStatus,
        });
      }
    }
  }
}

interface PipelinePosition {
  type: "building" | "deploying" | "awaiting-approval" | "idle";
  stage?: Stage;
  deployedStage?: Stage;
  awaitingStage?: Stage;
}

/**
 * Analyze the pipeline stages to determine exactly where things stand.
 */
function analyzeCurrentPipelinePosition(
  stages: Awaited<ReturnType<typeof getPipelineState>>
): PipelinePosition {
  // Check if an approval stage is in progress (waiting for approval)
  const waitingApproval = stages.find(
    (s) => s.stageName.includes("Approve") && s.status === "InProgress"
  );

  if (waitingApproval) {
    // Pipeline is waiting at an approval gate
    // The version is deployed at the stage BEFORE this approval
    const deployedStage = getStageBeforeApproval(waitingApproval.stageName);
    const awaitingStage = getStageAfterApproval(waitingApproval.stageName);
    return {
      type: "awaiting-approval",
      deployedStage,
      awaitingStage,
    };
  }

  // Check if a deploy stage is actively running
  const deployingStage = stages.find(
    (s) => s.stageName.includes("Deploy") && s.status === "InProgress"
  );

  if (deployingStage) {
    const stage = stageFromName(deployingStage.stageName);
    if (stage) return { type: "deploying", stage };
  }

  // Check if build/source is in progress
  const building = stages.find(
    (s) => (s.stageName === "Build" || s.stageName === "Source") && s.status === "InProgress"
  );

  if (building) return { type: "building" };

  return { type: "idle" };
}

/**
 * Get the stage that is deployed BEFORE an approval gate.
 * e.g., Approve_Beta → deployed at "dev"
 *        Approve_Prod → deployed at "beta"
 */
function getStageBeforeApproval(approvalStageName: string): Stage {
  if (approvalStageName.includes("Prod")) return "beta";
  if (approvalStageName.includes("Beta")) return "dev";
  return "dev";
}

/**
 * Get the stage that the approval gate leads TO.
 * e.g., Approve_Beta → "beta"
 *        Approve_Prod → "prod"
 */
function getStageAfterApproval(approvalStageName: string): Stage {
  if (approvalStageName.includes("Prod")) return "prod";
  if (approvalStageName.includes("Beta")) return "beta";
  return "beta";
}

/**
 * Extract stage from a deploy stage name.
 */
function stageFromName(stageName: string): Stage | null {
  if (stageName.includes("Prod")) return "prod";
  if (stageName.includes("Beta")) return "beta";
  if (stageName.includes("Dev")) return "dev";
  return null;
}

/**
 * Find the last deploy stage that succeeded.
 */
function getLastSucceededDeployStage(
  stages: Awaited<ReturnType<typeof getPipelineState>>
): Stage | null {
  const deployStages = stages.filter((s) => s.stageName.includes("Deploy"));

  // Check in reverse order (prod first)
  for (const stage of [...deployStages].reverse()) {
    if (stage.status === "Succeeded") {
      return stageFromName(stage.stageName);
    }
  }

  return null;
}
